import React, { useState, useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { RiFileList3Line, RiDownloadLine, RiSearchLine, RiRefreshLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Skeleton } from '@components/ui/Skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@components/ui/Table';
import { formatPrice } from '@/utils';

/**
 * TaxReports Page
 * Displays tax information from quotes in a datatable format
 */
const TaxReports = () => {
  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ startDate: '', endDate: '' });

  // Ensure user profile is loaded
  React.useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  const profileId = userProfile?.userprofile_id;

  // Fetch quotes data
  const { data: quotesData, isLoading, refetch } = useQuery({
    queryKey: ['tax-reports', profileId],
    queryFn: async () => {
      const filters = { created_by: profileId, status: "ACCEPTED" };
      const data = await apiClient.getQuotes(0, 100, filters);
      
        console.log("quotesDatareport", data);

      // Filter quotes that have at least one shortlisted bid option
      /*const quotesWithShortlisted = data.filter(quote => {
        const bid = quote.bid_items?.[0]?.bid;
        const allBidOptions = bid?.bid_options || [];
        return allBidOptions.some(option => option.shortlisted === true || option.shortlisted === 1);
      });*/

      // Transform and extract tax data from filtered quotes
      return data.map(quote => {
        const bid = quote.bid_items?.[0]?.bid;
        const bidOptions = bid?.bid_options || [];

        // Sum up values from shortlisted bid options only
        const totalFET = bidOptions.reduce((sum, option) => sum + (parseFloat(option.federal_excise_tax) || 0), 0);
        //const totalSegmentTax = bidOptions.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0);
        //const passengerSegments = bidOptions.reduce((sum, option) => sum + (parseFloat(option.segment_pax_count) || 0), 0);


        const totalSegmentTax = quote.segment_tax || bidOptions.reduce((sum, option) => sum + (parseFloat(option.segment_tax) || 0), 0);
        const passengerSegments = quote.passenger_count || 0;

        // Use quote.federal_excise_tax only if it's a number, otherwise use calculated value
        const quoteFET = typeof quote.federal_excise_tax === 'number' ? quote.federal_excise_tax : parseFloat(quote.federal_excise_tax) || 0;
        const finalFET = quoteFET || totalFET;

        return {
          quote_id: quote.quote_id,
          trip_id: bid?.trip_number || bid?.bid_number || `T-${quote.quote_id?.slice(0, 6)}`,
          departure_date: bid?.departure_date || quote.created_at,
          total_taxable_amount: parseFloat(quote.total_due) || 0,
          federal_excise_tax: finalFET,
          passenger_segments: passengerSegments,
          segment_tax: totalSegmentTax,
          total_taxes: finalFET + totalSegmentTax,
        };
      });
    },
    enabled: !!profileId,
    staleTime: 0,
    refetchOnMount: 'always',
  });



  // Filter data by date range
  const filteredData = useMemo(() => {
    if (!quotesData) return [];

    return quotesData.filter(quote => {
      if (!appliedFilters.startDate && !appliedFilters.endDate) return true;

      const quoteDate = new Date(quote.departure_date);
      const start = appliedFilters.startDate ? new Date(appliedFilters.startDate) : null;
      const end = appliedFilters.endDate ? new Date(appliedFilters.endDate) : null;

      if (start && end) {
        return quoteDate >= start && quoteDate <= end;
      } else if (start) {
        return quoteDate >= start;
      } else if (end) {
        return quoteDate <= end;
      }
      return true;
    });
  }, [quotesData, appliedFilters]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!filteredData.length) return { totalTaxable: 0, totalFET: 0, totalSegments: 0, totalSegmentFee: 0, totalTaxes: 0 };

    return filteredData.reduce((acc, quote) => ({
      totalTaxable: acc.totalTaxable + quote.total_taxable_amount,
      totalFET: acc.totalFET + quote.federal_excise_tax,
      totalSegments: acc.totalSegments + quote.passenger_segments,
      totalSegmentFee: acc.totalSegmentFee + quote.segment_tax,
      totalTaxes: acc.totalTaxes + quote.total_taxes,
    }), { totalTaxable: 0, totalFET: 0, totalSegments: 0, totalSegmentFee: 0, totalTaxes: 0 });
  }, [filteredData]);

  // Format date for display (DD-MMM-YY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Handle search
  const handleSearch = () => {
    setAppliedFilters({ startDate, endDate });
  };

  // Handle reset
  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setAppliedFilters({ startDate: '', endDate: '' });
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    if (!filteredData.length) return;

    const headers = ['First Leg Dept Date', 'Trip ID', 'Total Taxable Amount', 'FET on Trip', 'Passenger Segments', 'Total Segment Fee', 'Total Taxes'];
    const rows = filteredData.map(quote => [
      formatDisplayDate(quote.departure_date),
      quote.trip_id,
      quote.total_taxable_amount.toFixed(2),
      quote.federal_excise_tax.toFixed(2),
      quote.passenger_segments,
      quote.segment_tax.toFixed(2),
      quote.total_taxes.toFixed(2),
    ]);

    // Add totals row
    rows.push([
      'TOTALS',
      '',
      totals.totalTaxable.toFixed(2),
      totals.totalFET.toFixed(2),
      totals.totalSegments,
      totals.totalSegmentFee.toFixed(2),
      totals.totalTaxes.toFixed(2),
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `tax-report-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="pb-20">
      {/* Date Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              <RiSearchLine className="w-4 h-4" />
              Search
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RiRefreshLine className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleDownloadCSV}
              disabled={!filteredData.length}
              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiDownloadLine className="w-4 h-4" />
              Download (CSV)
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredData?.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RiFileList3Line className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tax data found</h3>
              <p className="text-sm text-gray-500">Tax information will appear here once you have quotes.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead>First Leg Dept Date</TableHead>
                  <TableHead>Trip ID</TableHead>
                  <TableHead className="text-right">Total Taxable Amount</TableHead>
                  <TableHead className="text-right">FET on Trip</TableHead>
                  <TableHead className="text-right">Passenger Segments</TableHead>
                  <TableHead className="text-right">Total Segment Fee</TableHead>
                  <TableHead className="text-right">Total Taxes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData?.map((quote) => (
                  <TableRow key={quote.quote_id}>
                    <TableCell>{formatDisplayDate(quote.departure_date)}</TableCell>
                    <TableCell className="font-medium">{quote.trip_id}</TableCell>
                    <TableCell className="text-right">{formatPrice(quote.total_taxable_amount)}</TableCell>
                    <TableCell className="text-right">{formatPrice(quote.federal_excise_tax)}</TableCell>
                    <TableCell className="text-right">{quote.passenger_segments}</TableCell>
                    <TableCell className="text-right">{formatPrice(quote.segment_tax)}</TableCell>
                    <TableCell className="text-right">{formatPrice(quote.total_taxes)}</TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-gray-100 font-semibold">
                  <TableCell colSpan={2}>Totals</TableCell>
                  <TableCell className="text-right">{formatPrice(totals.totalTaxable)}</TableCell>
                  <TableCell className="text-right">{formatPrice(totals.totalFET)}</TableCell>
                  <TableCell className="text-right">{totals.totalSegments}</TableCell>
                  <TableCell className="text-right">{formatPrice(totals.totalSegmentFee)}</TableCell>
                  <TableCell className="text-right">{formatPrice(totals.totalTaxes)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxReports;
